import React, { createContext, useContext, useState, useCallback, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { FiCheck, FiX, FiAlertCircle, FiInfo, FiAlertTriangle } from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'
import { hapticSuccess, hapticError, hapticWarning } from '../services/hapticsService'

const ToastContext = createContext({})

export const useToast = () => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within ToastProvider')
  }
  return context
}

// Default deduplication window in milliseconds
const DEDUP_WINDOW_MS = 5000

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([])
  // Track recently shown messages to prevent duplicates
  const recentMessages = useRef(new Map())

  const addToast = useCallback((toast) => {
    const message = toast.message
    const now = Date.now()

    // Check if this exact message was shown recently (within dedup window)
    const lastShown = recentMessages.current.get(message)
    if (lastShown && now - lastShown < DEDUP_WINDOW_MS) {
      // Skip duplicate toast
      return null
    }

    // Record this message timestamp
    recentMessages.current.set(message, now)

    // Clean up old entries periodically (keep map from growing indefinitely)
    if (recentMessages.current.size > 50) {
      const cutoff = now - DEDUP_WINDOW_MS
      for (const [msg, timestamp] of recentMessages.current.entries()) {
        if (timestamp < cutoff) {
          recentMessages.current.delete(msg)
        }
      }
    }

    const id = now + Math.random()
    const newToast = {
      id,
      type: 'info',
      duration: 5000,
      ...toast,
    }

    setToasts(prev => [...prev, newToast])

    // Auto remove toast after duration
    if (newToast.duration > 0) {
      setTimeout(() => {
        removeToast(id)
      }, newToast.duration)
    }

    return id
  }, [])

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id))
  }, [])

  const success = useCallback((message, options = {}) => {
    // Trigger haptic feedback on native platforms
    hapticSuccess()
    return addToast({
      type: 'success',
      message,
      ...options
    })
  }, [addToast])

  const error = useCallback((message, options = {}) => {
    // Trigger haptic feedback on native platforms
    hapticError()
    return addToast({
      type: 'error',
      message,
      duration: 7000, // Keep error messages longer
      ...options
    })
  }, [addToast])

  const warning = useCallback((message, options = {}) => {
    // Trigger haptic feedback on native platforms
    hapticWarning()
    return addToast({
      type: 'warning',
      message,
      ...options
    })
  }, [addToast])

  const info = useCallback((message, options = {}) => {
    return addToast({
      type: 'info',
      message,
      ...options
    })
  }, [addToast])

  const value = {
    toasts,
    addToast,
    removeToast,
    success,
    error,
    warning,
    info
  }

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  )
}

/**
 * Toast container with responsive positioning:
 * - Mobile (<640px): Bottom-centre, above bottom navigation, respecting safe area
 * - Desktop (>=640px): Top-right, respecting safe area insets for iOS PWA
 *
 * Safe area insets handle iOS notch/Dynamic Island and home indicator.
 * z-index set to 60 to appear above modals and navigation.
 */
const ToastContainer = ({ toasts, removeToast }) => {
  return (
    <div
      className="fixed z-[60] space-y-2 max-w-sm w-full px-4 bottom-24 left-0 right-0 mx-auto sm:bottom-auto sm:left-auto sm:right-4 sm:mx-0 sm:px-0"
      style={{
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        top: 'var(--toast-top-position)'
      }}
    >
      <style>{`
        @media (min-width: 640px) {
          :root { --toast-top-position: max(1rem, calc(env(safe-area-inset-top, 0px) + 1rem)); }
        }
        @media (max-width: 639px) {
          :root { --toast-top-position: auto; }
        }
      `}</style>
      <AnimatePresence>
        {toasts.map((toast) => (
          <Toast key={toast.id} toast={toast} onRemove={removeToast} />
        ))}
      </AnimatePresence>
    </div>
  )
}

/**
 * Toast component with solid backgrounds for readability.
 * Uses white background with coloured left border to maintain
 * status colour coding whilst ensuring text is always readable.
 */
const Toast = ({ toast, onRemove }) => {
  const getToastConfig = (type) => {
    switch (type) {
      case 'success':
        return {
          icon: FiCheck,
          iconColor: 'text-status-success',
          bgColor: 'bg-white',
          borderColor: 'border-l-4 border-l-status-success border border-neutral-200',
          titleColor: 'text-status-success',
          textColor: 'text-neutral-700'
        }
      case 'error':
        return {
          icon: FiX,
          iconColor: 'text-status-error',
          bgColor: 'bg-white',
          borderColor: 'border-l-4 border-l-status-error border border-neutral-200',
          titleColor: 'text-status-error',
          textColor: 'text-neutral-700'
        }
      case 'warning':
        return {
          icon: FiAlertTriangle,
          iconColor: 'text-status-warning',
          bgColor: 'bg-white',
          borderColor: 'border-l-4 border-l-status-warning border border-neutral-200',
          titleColor: 'text-status-warning',
          textColor: 'text-neutral-700'
        }
      case 'info':
      default:
        return {
          icon: FiInfo,
          iconColor: 'text-accent',
          bgColor: 'bg-white',
          borderColor: 'border-l-4 border-l-accent border border-neutral-200',
          titleColor: 'text-accent',
          textColor: 'text-neutral-700'
        }
    }
  }

  const config = getToastConfig(toast.type)

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.95 }}
      transition={{ type: "spring", stiffness: 400, damping: 25 }}
      className={`relative overflow-hidden rounded-xl shadow-lg ${config.bgColor} ${config.borderColor}`}
    >
      {/* Progress bar for auto-dismiss */}
      {toast.duration > 0 && (
        <motion.div
          initial={{ width: '100%' }}
          animate={{ width: '0%' }}
          transition={{ duration: toast.duration / 1000, ease: 'linear' }}
          className="absolute top-0 left-0 h-1 bg-current opacity-30"
        />
      )}
      
      <div className="p-4">
        <div className="flex items-start space-x-3">
          {/* Icon */}
          <div className={`flex-shrink-0 ${config.iconColor}`}>
            <SafeIcon icon={config.icon} className="w-5 h-5" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {toast.title && (
              <h4 className={`text-sm font-semibold ${config.titleColor} mb-1`}>
                {toast.title}
              </h4>
            )}
            <p className={`text-sm ${config.textColor}`}>
              {toast.message}
            </p>
            
            {toast.action && (
              <button
                onClick={toast.action.onClick}
                className={`mt-2 text-sm font-medium underline ${config.titleColor} hover:opacity-80 transition-opacity`}
              >
                {toast.action.label}
              </button>
            )}
          </div>

          {/* Close button */}
          <button
            onClick={() => onRemove(toast.id)}
            className={`flex-shrink-0 text-neutral-400 hover:text-neutral-600 transition-colors`}
          >
            <SafeIcon icon={FiX} className="w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.div>
  )
}

export default ToastProvider